import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Save, RefreshCw, FileJson, RotateCcw } from 'lucide-react';
import { getLmsContent, deepMerge } from '../../data/lmsContent';

/**
 * LMS copy — raw JSON editor (content store, no redeploy).
 * Covers the paywall marketing copy + PayPal confirm/cancel/error messages and
 * the phoneme library microcopy. Loads the EFFECTIVE copy (JS defaults deep-
 * merged with the backend override) then PUTs the full edited object.
 */
export const AdminLmsCopyTab = ({ backendUrl, token, showToast }) => {
  const defaults = getLmsContent('it');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setJsonError('');
    try {
      const res = await axios.get(`${backendUrl}/api/lms/copy`);
      const merged = deepMerge(getLmsContent('it'), res.data || {});
      setText(JSON.stringify(merged, null, 2));
    } catch (e) {
      setText(JSON.stringify(getLmsContent('it'), null, 2));
      showToast?.('Override non caricato, mostro i default', 'error');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, showToast]);

  useEffect(() => { load(); }, [load]);

  const resetToDefaults = () => {
    setText(JSON.stringify(defaults, null, 2));
    setJsonError('');
  };

  const save = async () => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setJsonError(`JSON non valido: ${e.message}`);
      return;
    }
    setJsonError('');
    setSaving(true);
    try {
      await axios.put(
        `${backendUrl}/api/admin/lms/copy`,
        parsed,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showToast?.('Copy LMS salvata — attiva subito, senza rideploy', 'success');
    } catch (e) {
      showToast?.(e?.response?.data?.detail || 'Salvataggio fallito', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-lms-copy-tab">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-white">
          <FileJson className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-bold">Copy Paywall Premium (JSON)</h2>
        </div>
        <div className="flex gap-2">
          <Button onClick={resetToDefaults} className="bg-slate-700 hover:bg-slate-600" data-testid="admin-lms-copy-reset">
            <RotateCcw className="w-4 h-4 mr-2" /> Default
          </Button>
          <Button onClick={load} disabled={loading} className="bg-slate-700 hover:bg-slate-600" data-testid="admin-lms-copy-reload">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Ricarica
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="admin-lms-copy-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvo…' : 'Salva'}
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-400">
        Testi del paywall premium (titolo, sottotitolo, benefici, conferma/annullamento/errore PayPal, consenso).
        Placeholder: <code className="text-cyan-300">{'{N}'}</code> = n° card pubblicate, <code className="text-cyan-300">{'{piano}'}</code> = piano scelto, <code className="text-cyan-300">{'{email}'}</code> = email del lead.
        Le modifiche vanno live subito, senza rideploy. Mantieni la struttura delle chiavi.
      </p>

      {jsonError && (
        <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="admin-lms-copy-error">{jsonError}</div>
      )}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        className="w-full h-[520px] px-4 py-3 rounded-xl border border-slate-700 bg-slate-950/70 text-slate-100 font-mono text-xs leading-relaxed focus:border-orange-400 outline-none"
        data-testid="admin-lms-copy-textarea"
      />
    </div>
  );
};

export default AdminLmsCopyTab;
